import { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Box, Typography, Button, IconButton, Stack, Chip, TextField, FormControl, InputLabel, Select, MenuItem, Menu, Link, TablePagination, Paper, Tooltip } from "@mui/material";
import GenericDataTable from "../components/GenericDataTable";
import TicketModal from "../components/TicketModal";
import { getTickets, addTicket, updateTicket, deleteTicket } from "../Services/ticketsService";
import { getClients } from "../Services/clientsService";
import { getContacts } from "../Services/contactsService";
import { getCollaborateurs } from "../Services/collaborateursService";
import { getStatuts } from "../Services/statutsService";
import normalizedDataService from "../Services/normalizedDataService";
import { uploadTicketAttachment, getTicketAttachments, deleteTicketAttachment } from "../Services/attachmentsService";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/date";
import { useAuth } from "../context/AuthContext";

const baseColumns = [
  { field: "numeroTicket", headerName: "N°" },
  { field: "titre", headerName: "Titre" },
  { field: "clientName", headerName: "Client" },
  { field: "collaborateurName", headerName: "Collaborateur" },
  { field: "prioriteLabel", headerName: "Gravité/Priorité" },
  { field: "tpsResolution", headerName: "Temps (min)" },
  { field: "dateCreation", headerName: "Créé le" },
  { field: "statutLabel", headerName: "Statut" },
  { field: "actions", headerName: "" },
];
const FILTERS_STORAGE_KEY = "ticketsPage.filters";

function extractIdFromIri(iri) {
  if (typeof iri === "string") {
    const parts = iri.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  }
  if (iri && iri.id) return String(iri.id);
  return "";
}

function mapTicketForUI(ticket, statutsMap, clientsMap = {}, collaborateursMap = {}) {
  const statutId = extractIdFromIri(ticket.statutId);
  const statutLabel = statutsMap[statutId] || statutId || "";
  const prioriteValue = ticket.priorite != null && ticket.priorite !== ""
    ? Number(ticket.priorite)
    : null;
  const prioriteLabel = prioriteValue === 1
    ? "Critique / Urgente"
    : prioriteValue === 2
      ? "Haute"
      : prioriteValue === 3
        ? "Moyenne"
        : prioriteValue === 4
          ? "Basse"
          : "";
  const clientId = extractIdFromIri(ticket.clientId);
  const clientName =
    clientsMap[clientId] ||
    (ticket.client && (ticket.client.nom || ticket.client.name)) ||
    "";
  const collaborateurId = extractIdFromIri(ticket.collaborateurId);
  let collaborateurName = "";
  if (collaborateurId) {
    const collaborateur = collaborateursMap[collaborateurId];
    if (collaborateur) {
      collaborateurName = `${collaborateur.prenom || ''} ${collaborateur.nom || ''}`.trim();
    } else if (ticket.collaborateur) {
      collaborateurName = `${ticket.collaborateur.prenom || ''} ${ticket.collaborateur.nom || ''}`.trim();
    }
  }
  return {
    ...ticket,
    statut: statutId,
    statutLabel,
    prioriteLabel,
    clientId,
    clientName,
    collaborateurId,
    collaborateurName,
  };
}

function chipColorForPriorite(value) {
  switch (Number(value)) {
    case 1:
      return "error";
    case 2:
      return "warning";
    case 3:
      return "info";
    case 4:
      return "success";
    default:
      return "default";
  }
}

function chipColorForStatut(label) {
  if (!label) return 'default';
  const l = label.toLowerCase();
  if (l.includes('demande')) return 'default';
  if (l.includes('affect')) return 'info';
  if (l.includes('cours')) return 'warning';
  if (l.includes('attente')) return 'secondary';
  if (l.includes('termin')) return 'success';
  if (l.includes('réouvr') || l.includes('reouvr')) return 'error';
  return 'default';
}

export default function TicketsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialQueryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const savedFilters = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }, []);
  const queryOrSaved = (queryKey, savedKey, fallback = "") => {
    const queryValue = initialQueryParams.get(queryKey);
    if (queryValue !== null) return queryValue;
    const savedValue = savedFilters[savedKey];
    if (savedValue == null) return fallback;
    return String(savedValue);
  };
  const parsePositiveInt = (value, fallback) => {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isNaN(parsed) || parsed < 0 ? fallback : parsed;
  };
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [statutsMap, setStatutsMap] = useState({});
  const [clientsMap, setClientsMap] = useState({});
  const [collaborateursMap, setCollaborateursMap] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [search, setSearch] = useState(queryOrSaved("search", "search", ""));
  const [statutFilter, setStatutFilter] = useState(queryOrSaved("statut", "statutFilter", ""));
  const [clientFilter, setClientFilter] = useState(queryOrSaved("client", "clientFilter", ""));
  const [collaborateurFilter, setCollaborateurFilter] = useState(queryOrSaved("collaborateur", "collaborateurFilter", ""));
  const [attMenuAnchor, setAttMenuAnchor] = useState(null);
  const [attMenuTicketId, setAttMenuTicketId] = useState(null);
  const [attMenuItems, setAttMenuItems] = useState([]);
  const [page, setPage] = useState(parsePositiveInt(queryOrSaved("page", "page", "0"), 0));
  const [rowsPerPage, setRowsPerPage] = useState(parsePositiveInt(queryOrSaved("rowsPerPage", "rowsPerPage", "10"), 10));
  const { showToast } = useToast();
  const { user } = useAuth();
  const isCollaborateur = user?.isCollaborateur();

  const columns = useMemo(() => {
    return baseColumns.map((c) => {
      if (c.field === 'tpsResolution') {
        return {
          ...c,
          renderCell: (row) =>
            row.tpsResolution != null && row.tpsResolution !== ""
              ? `${row.tpsResolution} min`
              : "",
        };
      }
      if (c.field === 'prioriteLabel') {
        return {
          ...c,
          renderCell: (row) => (
            <Chip
              label={row.prioriteLabel || ''}
              color={chipColorForPriorite(row.priorite)}
              size="small"
            />
          ),
        };
      }
      if (c.field === 'statutLabel') {
        return { ...c, renderCell: (row) => <Chip label={row.statutLabel} color={chipColorForStatut(row.statutLabel)} size="small" /> };
      }
      if (c.field === "collaborateurName") {
        return {
          ...c,
          renderCell: (row) => (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.collaborateurName || "-"}
            </Typography>
          ),
        };
      }
      if (c.field === 'dateCreation') {
        return { ...c, renderCell: (row) => formatDate(row.dateCreation) };
      }
      return c;
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [st, cl, ct, co, tk] = await Promise.all([
          getStatuts(),
          getClients(),
          getContacts(),
          getCollaborateurs(),
          getTickets(),
        ]);
        if (!mounted) return;
        setStatuts(st);
        const smap = st.reduce((acc, s) => {
          acc[String(s.id)] = s.libelle || String(s.id);
          return acc;
        }, {});
        setStatutsMap(smap);
        const cmap = cl.reduce((acc, c) => {
          acc[String(c.id)] = c.nom || String(c.id);
          return acc;
        }, {});
        setClientsMap(cmap);
        const colmap = co.reduce((acc, col) => {
          acc[String(col.id)] = col;
          return acc;
        }, {});
        setCollaborateursMap(colmap);
        setClients(cl);
        setContacts(ct);
        setCollaborateurs(co);
        setTickets(tk.map((t) => mapTicketForUI(t, smap, cmap, colmap)));
      } catch (e) {
        if (!mounted) return;
        setTickets([]);
        showToast("Erreur lors du chargement des tickets", { severity: 'error' });
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (location.state && location.state.openCreate) {
      setEditData(null);
      setModalOpen(true);
    }
  }, [location.state]);

  // Effet séparé pour gérer les filtres après le chargement des statuts
  useEffect(() => {
    if (location.state && location.state.filter && statuts.length > 0) {
      if (statutFilter) return;
      // Appliquer le filtre basé sur le type de métrique
      const filter = location.state.filter;
      
      switch (filter) {
        case 'all':
          setStatutFilter('');
          break;
        case 'inProgress':
          // Filtrer par statuts "en cours"
          const inProgressStatuts = statuts.filter(s => 
            s.libelle && (
              s.libelle.toLowerCase().includes('cours') || 
              s.libelle.toLowerCase().includes('intervention') ||
              s.libelle.toLowerCase().includes('affecté')
            )
          );
          if (inProgressStatuts.length > 0) {
            setStatutFilter(inProgressStatuts[0].id);
          }
          break;
        case 'completed':
          // Filtrer par statuts "terminé"
          const completedStatuts = statuts.filter(s => 
            s.libelle && s.libelle.toLowerCase().includes('terminée')
          );
          if (completedStatuts.length > 0) {
            setStatutFilter(completedStatuts[0].id);
          }
          break;
        case 'waiting':
          // Filtrer par statuts "en attente"
          const waitingStatuts = statuts.filter(s => 
            s.libelle && s.libelle.toLowerCase().includes('attente')
          );
          if (waitingStatuts.length > 0) {
            setStatutFilter(waitingStatuts[0].id);
          }
          break;
        default:
          setStatutFilter('');
      }
    }
  }, [location.state, statuts, statutFilter]);

  // Réinitialiser la page quand les filtres ou la recherche changent
  useEffect(() => {
    setPage(0);
  }, [search, statutFilter, clientFilter, collaborateurFilter]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({
          search,
          statutFilter,
          clientFilter,
          collaborateurFilter,
          page,
          rowsPerPage,
        })
      );
    } catch (e) {
      // Ignorer les erreurs de stockage local/session.
      console.error(e);
    }

    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statutFilter) params.set("statut", String(statutFilter));
    if (clientFilter) params.set("client", String(clientFilter));
    if (collaborateurFilter) params.set("collaborateur", String(collaborateurFilter));
    if (page > 0) params.set("page", String(page));
    if (rowsPerPage !== 10) params.set("rowsPerPage", String(rowsPerPage));

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;
    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        {
          replace: true,
          state: location.state,
        }
      );
    }
  }, [
    search,
    statutFilter,
    clientFilter,
    collaborateurFilter,
    page,
    rowsPerPage,
    navigate,
    location.pathname,
    location.search,
    location.state,
  ]);

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    if (!user?.canEditTickets()) return;
    setEditData(row);
    setModalOpen(true);
  };

  const handleDelete = (row) => {
    if (!user?.canDeleteTickets()) return;
    deleteTicket(row.id)
      .then(() => {
        setTickets(tickets.filter((t) => t.id !== row.id));
        showToast("Ticket supprimé", { severity: 'success' });
      })
      .catch(() => showToast("Suppression échouée", { severity: 'error' }));
  };

  const uploadFiles = async (ticketId, files) => {
    try {
      for (const f of files) {
        await uploadTicketAttachment(ticketId, f);
      }
      showToast("Pièces jointes envoyées", { severity: 'success' });
    } catch (e) {
      showToast("Échec de l'upload des pièces jointes", { severity: 'error' });
    }
  };

  const handleSave = (form) => {
    if (editData) {
      return updateTicket(editData.id, form)
        .then((updated) => {
          const mapped = mapTicketForUI(updated, statutsMap, clientsMap, collaborateursMap);
          setTickets(tickets.map((t) => (t.id === mapped.id ? mapped : t)));
          setModalOpen(false);
          showToast("Ticket mis à jour", { severity: 'success' });
          return mapped;
        })
        .catch(() => { showToast("Mise à jour échouée", { severity: 'error' }); });
    } else {
      return addTicket(form)
        .then((created) => {
          const mapped = mapTicketForUI(created, statutsMap, clientsMap, collaborateursMap);
          setTickets([...tickets, mapped]);
          setModalOpen(false);
          showToast("Ticket créé", { severity: 'success' });
          return mapped;
        })
        .catch(() => { showToast("Création échouée", { severity: 'error' }); });
    }
  };

  const openAttachmentsMenu = async (event, ticket) => {
    setAttMenuAnchor(event.currentTarget);
    setAttMenuTicketId(ticket.id);
    try {
      const items = await getTicketAttachments(ticket.id);
      setAttMenuItems(items);
    } catch {
      setAttMenuItems([]);
    }
  };

  const closeAttachmentsMenu = () => {
    setAttMenuAnchor(null);
    setAttMenuTicketId(null);
    setAttMenuItems([]);
  };

  const handleDeleteAttachment = async (att) => {
    try {
      await deleteTicketAttachment(att.id);
      setAttMenuItems(attMenuItems.filter((a) => a.id !== att.id));
      showToast("Pièce jointe supprimée", { severity: 'success' });
    } catch {
      showToast("Échec de la suppression", { severity: 'error' });
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const result = tickets.filter((t) => {
      const matchSearch = !s || (String(t.numeroTicket).toLowerCase().includes(s) || String(t.titre || '').toLowerCase().includes(s));
      const matchStatut = !statutFilter || String(t.statut) === String(statutFilter);
      const matchClient = !clientFilter || String(t.clientId) === String(clientFilter);
      const matchCollaborateur = !collaborateurFilter || String(t.collaborateurId) === String(collaborateurFilter);
      return matchSearch && matchStatut && matchClient && matchCollaborateur;
    });
    // Tri du plus récent au plus ancien
    return result.sort((a, b) => {
      const da = a.dateCreation ? new Date(a.dateCreation) : 0;
      const db = b.dateCreation ? new Date(b.dateCreation) : 0;
      return db - da;
    });
  }, [tickets, search, statutFilter, clientFilter, collaborateurFilter]);

  const rowsWithActions = filtered.map((t) => ({
    ...t,
    actions: (
      <Stack direction="row" spacing={1}>
        <Tooltip title="Voir le ticket">
          <IconButton
            size="small"
            onClick={() => navigate(`/tickets/${t.id}`)}
            sx={{ bgcolor: "action.hover", "&:hover": { bgcolor: "action.selected" } }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Voir les pièces jointes">
          <IconButton
            size="small"
            onClick={(e) => openAttachmentsMenu(e, t)}
            sx={{ bgcolor: "action.hover", "&:hover": { bgcolor: "action.selected" } }}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
        {user?.permissions?.canDeleteTickets && (
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(t)}
              sx={{ bgcolor: "rgba(211, 47, 47, 0.08)", "&:hover": { bgcolor: "rgba(211, 47, 47, 0.18)" } }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    ),
  }));

  const paginatedRows = useMemo(
    () => rowsWithActions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rowsWithActions, page, rowsPerPage]
  );

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Gestion des tickets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Gérez vos demandes d'assistance, assignez les collaborateurs et suivez l'avancement.
        </Typography>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            position: "sticky",
            top: 8,
            zIndex: 2,
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField label="Rechercher (N° ou Titre)" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="client-filter-label">Filtrer par client</InputLabel>
              <Select labelId="client-filter-label" label="Filtrer par client" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {clients.sort((a, b) => (a.nom || '').localeCompare(b.nom || '')).map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.nom}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="collaborateur-filter-label">Filtrer par collaborateur</InputLabel>
              <Select labelId="collaborateur-filter-label" label="Filtrer par collaborateur" value={collaborateurFilter} onChange={(e) => setCollaborateurFilter(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {collaborateurs.sort((a, b) => {
                  const nameA = `${a.prenom || ''} ${a.nom || ''}`.trim();
                  const nameB = `${b.prenom || ''} ${b.nom || ''}`.trim();
                  return nameA.localeCompare(nameB);
                }).map((col) => (
                  <MenuItem key={col.id} value={String(col.id)}>{`${col.prenom || ''} ${col.nom || ''}`.trim()}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="statut-filter-label">Filtrer par statut</InputLabel>
              <Select labelId="statut-filter-label" label="Filtrer par statut" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {statuts.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.libelle}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={handleAdd}>
              Demande d'assistance
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearch("");
                setClientFilter("");
                setCollaborateurFilter("");
                setStatutFilter("");
              }}
            >
              Réinitialiser
            </Button>
          </Stack>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {rowsWithActions.length} ticket(s) affiché(s)
        </Typography>
        <GenericDataTable 
          columns={columns} 
          rows={paginatedRows}
          bordered
          headerAlign="center"
          cellAlign="center"
          headerSx={{ fontSize: 14, backgroundColor: 'primary.main', color: 'primary.contrastText' }}
          cellSx={{ fontSize: 13, py: 1.25 }}
          sx={{ borderRadius: 2 }}
        />
        <TablePagination
          component="div"
          count={rowsWithActions.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
        <Menu anchorEl={attMenuAnchor} open={Boolean(attMenuAnchor)} onClose={closeAttachmentsMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
          {attMenuItems.length === 0 && <MenuItem disabled>Aucune pièce jointe</MenuItem>}
          {attMenuItems.map((att) => (
            <MenuItem key={att.id} sx={{ display: 'flex', gap: 1 }}>
              <Link href={att.url} target="_blank" rel="noopener" underline="hover" sx={{ flex: 1 }}>{att.originalName || att.fileName}</Link>
              {user?.permissions?.canDeleteTickets && (
                <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </MenuItem>
          ))}
        </Menu>
        <TicketModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          onUploadFiles={uploadFiles}
          initialData={editData}
          clients={clients}
          contacts={contacts}
          collaborateurs={collaborateurs}
          statuts={statuts}
        />
      </Box>
    </div>
  );
} 